// src/services/apiClient.js
import axios from 'axios';

// Конфигурация API
const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://api.light-platform.com/api/v1';

console.log('🔧 API Client initialized:', API_BASE_URL);

// Создание экземпляра axios
const apiClient = axios.create({
    baseURL: API_BASE_URL,
    timeout: 30000, // ✅ КРИТИЧНО: 30 секунд таймаут для P2P
    headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
    },
    withCredentials: true, // ✅ Для CORS с cookies
});

// Интерцептор для добавления токена авторизации
apiClient.interceptors.request.use(
    (config) => {
        // ✅ Проверяем разные варианты хранения токена
        const token = localStorage.getItem('auth_token')
            || localStorage.getItem('auth_token')
            || localStorage.getItem('auth_token');

        if (token) {
            config.headers.Authorization = `Bearer ${token}`;
            console.log('🔑 Token added to request:', config.url);
        } else {
            console.warn('⚠️ No token found for request:', config.url);
        }

        // ✅ Логирование запроса
        console.log('📤 API Request:', {
            method: config.method.toUpperCase(),
            url: config.url,
            baseURL: config.baseURL,
            fullURL: `${config.baseURL}${config.url}`,
            params: config.params,
            hasToken: !!token
        });

        return config;
    },
    (error) => {
        console.error('❌ Request Interceptor Error:', error);
        return Promise.reject(error);
    }
);

// Интерцептор для обработки ответов
apiClient.interceptors.response.use(
    (response) => {
        // ✅ Логирование успешного ответа
        console.log('📥 API Response:', {
            url: response.config.url,
            status: response.status,
            dataSize: JSON.stringify(response.data).length
        });

        // ✅ Возвращаем ответ в исходном формате
        return response;
    },
    (error) => {
        // ✅ Детальное логирование ошибки
        const errorDetails = {
            url: error.config?.url,
            method: error.config?.method,
            baseURL: error.config?.baseURL,
            params: error.config?.params,
            status: error.response?.status,
            statusText: error.response?.statusText,
            data: error.response?.data,
            message: error.message,
            code: error.code
        };

        console.error('❌ API Request Failed:', errorDetails);

        // ✅ Обработка сетевых ошибок
        if (!error.response) {
            console.error('🌐 Network Error:', errorDetails);

            // Проверяем тип ошибки
            if (error.code === 'ECONNABORTED') {
                return Promise.reject({
                    success: false,
                    message: 'Превышено время ожидания ответа от сервера (30 сек)',
                    error: 'timeout',
                    details: errorDetails
                });
            }

            return Promise.reject({
                success: false,
                message: 'Ошибка сети. Проверьте подключение к интернету или доступность сервера.',
                error: 'network',
                details: errorDetails
            });
        }

        // ✅ Обработка ошибок авторизации
        if (error.response?.status === 401) {
            console.warn('🔒 Unauthorized - clearing token and redirecting');
            localStorage.removeItem('auth_token');
            localStorage.removeItem('auth_token');
            localStorage.removeItem('auth_token');

            // Проверяем, не на странице ли авторизации уже
            if (!window.location.pathname.includes('/auth')) {
                window.location.href = '/auth';
            }
        }

        // ✅ Обработка CORS ошибок
        if (error.response?.status === 0 || error.message.includes('CORS')) {
            console.error('🚫 CORS Error detected');
            return Promise.reject({
                success: false,
                message: 'Ошибка доступа к API (CORS). Проверьте настройки сервера.',
                error: 'cors',
                details: errorDetails
            });
        }

        // ✅ Обработка 404
        if (error.response?.status === 404) {
            console.error('🔍 Endpoint not found:', error.config?.url);
            return Promise.reject({
                success: false,
                message: `Endpoint не найден: ${error.config?.url}`,
                error: 'not_found',
                details: errorDetails
            });
        }

        // ✅ Обработка 500
        if (error.response?.status === 500) {
            console.error('💥 Server Error:', error.response?.data);
            return Promise.reject({
                success: false,
                message: error.response?.data?.message || 'Внутренняя ошибка сервера',
                error: 'server_error',
                details: errorDetails
            });
        }

        // ✅ Возвращаем структурированную ошибку
        const errorData = error.response?.data || {
            success: false,
            message: 'Произошла неизвестная ошибка'
        };

        return Promise.reject({
            ...errorData,
            httpStatus: error.response?.status,
            details: errorDetails
        });
    }
);

// ✅ Проверка доступности API при инициализации
const checkApiHealth = async () => {
    try {
        const response = await fetch(`${API_BASE_URL}/public/health`, {
            method: 'GET',
            headers: {
                'Accept': 'application/json'
            }
        });

        if (response.ok) {
            const data = await response.json();
            console.log('✅ API Health Check:', data);
        } else {
            console.warn('⚠️ API Health Check Failed:', response.status);
        }
    } catch (error) {
        console.error('❌ API Health Check Error:', error.message);
    }
};

// Запускаем проверку здоровья API
checkApiHealth();

export default apiClient;
